// Server config, the draft, diffs and generation history (`06` §3.9,
// `SC-F600`–`SC-F614`, `SC-F619`). The *read* half; the actions that produce a
// new generation live in `api/publish.ts`.
//
// **The draft IS a generation, in state `draft`, one per instance.** It is not a
// per-user workspace and there are no branches: every maintainer on the
// organization edits the same object set, and the publish button ships all of
// it (`05` §4.4). The API does not solve that with locking or branching — both
// are v2 questions — it solves it by refusing to be silent, which is why
// [Draft] carries per-object attribution and an `authors[]` roll-up and why
// `api/publish.ts` requires acknowledgements. The minimum bar: **no maintainer
// can discard or publish a colleague's unfinished work without the API having
// told them, in the same response, that it is a colleague's work.**
//
// YAML is rendered, read-only and exportable. Every `*Yaml` function here
// returns text for `bn-code`, and none of them has a write counterpart.
//
// The types are `internal/publish`'s wire shapes — `view.go`'s `DraftView` and
// `GenerationView`, `classify.go`'s verdict projection, `gate.go`'s steps and
// findings, `routes.go`'s YAML view — because those JSON tags are the contract
// and a guessed client type renders fields nothing populates.

import { getJSON, getWithETag, query, request, sendJSON, type Page } from "./http.js";
import type { FindingSeverity, PolicyFinding, PolicyKind } from "./policies.js";

const base = (instanceId: string) => `/v1/instances/${encodeURIComponent(instanceId)}`;

// ───────────────────────────────────────────────────── server config ──

/**
 * ServerConfig is the customer-owned half of `server.yaml` plus the read-only
 * derived fields.
 *
 * `PUT` **rejects** control-plane-owned keys rather than ignoring them
 * (`SC-F613`): listen addresses, the admin token, the whole `audit.*` and
 * `datasources.*` blocks, TLS, trusted proxies, per-IP limits, the approval
 * webhook block and the MCP listener all answer `403 field_guarded` with
 * `details.fields` naming them. Silently dropping them would let a customer
 * believe they had changed the audit destination.
 *
 * `derived` is where the control plane's own decisions are visible read-only, so
 * the form can show what it renders without offering to edit it.
 */
export interface ServerConfig {
  /** Shape is owned by the form schema (`I3`); `kind: "ServerConfig"`. */
  spec: Record<string, unknown>;
  derived: Record<string, unknown>;
  /** The keys `SC-F613` guards. Render them disabled with the reason, never
   * absent — a hidden guarded key looks like a missing feature. */
  guarded_fields: string[];
  findings: PolicyFinding[];
  updated: string;
  updated_by_label: string;
}

/** getServerConfig reads the customer-owned fields plus the derived ones
 * (`SC-F612`). */
export function getServerConfig(instanceId: string): Promise<ServerConfig> {
  return getJSON<ServerConfig>(`${base(instanceId)}/server-config`);
}

/**
 * getServerConfigWithETag is [getServerConfig] plus the resource's `ETag`, for
 * the editor that is going to write it back.
 *
 * `06` §1.7 makes `ETag` + `If-Match` the concurrency story for every mutable
 * resource and requires the UI to send it, and the server config is the one
 * object in an instance that several maintainers edit through one form — so a
 * read that discards the tag is a form that silently overwrites a colleague's
 * limits. A route that answers no `ETag` yields the empty string here, and
 * [putServerConfig] then sends no `If-Match`: the degradation is "no optimistic
 * concurrency", not a request the server refuses.
 */
export function getServerConfigWithETag(
  instanceId: string,
): Promise<{ value: ServerConfig; etag: string }> {
  return getWithETag<ServerConfig>(`${base(instanceId)}/server-config`);
}

/**
 * putServerConfig replaces the customer-owned half (`SC-F612`, `SC-F614`). It
 * mirrors Sluice's own `ServerConfig.Validate()` before publish — unimplemented
 * secret providers, partial TLS blocks, tracing without an endpoint,
 * `audit.syslog` without an address, object lock without positive retention,
 * `datasources.reload: true` — because discovering those at container boot
 * instead of at publish time is an outage.
 */
export function putServerConfig(
  instanceId: string,
  spec: Record<string, unknown>,
  opts: { ifMatch?: string } = {},
): Promise<ServerConfig> {
  return sendJSON<ServerConfig>(`${base(instanceId)}/server-config`, "PUT", { spec }, opts);
}

/**
 * getServerConfigYaml renders `server.yaml`, read-only (`SC-F602`).
 *
 * **The rendered text is redacted and says so.** `admin.token` is replaced by a
 * fixed placeholder, because Sluice consumes it verbatim rather than through
 * `secret://` and this is a viewer-role route — without the redaction the
 * instance's admin credential would be readable by the least-privileged member
 * of the organization. The hashes are computed over the *real* bytes, so a
 * displayed digest still matches the deployed bundle.
 */
export async function getServerConfigYaml(
  instanceId: string,
): Promise<{ yaml: string; redacted: string[] }> {
  return getJSON<{ yaml: string; redacted: string[] }>(`${base(instanceId)}/server-config/yaml`);
}

/**
 * DRAIN_CLAMP_SECONDS is `bundle.DrainClamp` — the value both query timeouts are
 * rendered at, and the threshold linter rule L33 warns above.
 *
 * It is 8 and not Sluice's own 30 because Sluice's REST drain is a hard-coded
 * 10 s and `ShutdownTimeout` is **not configurable**
 * (`internal/transport/rest/server.go:204-205`, `internal/config/server.go:37-42`;
 * `02` §5.3, `SLUICE-U20`). A query allowed to run longer than the drain is
 * dropped on every restart-class publish, and every `server.yaml` change is
 * restart-class — so raising these two keys is a trade against restart behaviour
 * rather than a tuning knob. L33 is therefore a *blocking warning* cleared by an
 * acknowledgement recorded on the generation, not a refusal: a customer who
 * publishes rarely and queries slowly is making a legitimate choice.
 */
export const DRAIN_CLAMP_SECONDS = 8;

/**
 * GuardScope says why a `server.yaml` key is control-plane-owned, in the
 * customer's terms. It is `publish.GuardScope` verbatim and arrives on a
 * `403 field_guarded` under `details.reasons[path].scope`.
 *
 * The scopes are not interchangeable, which is the whole reason the type exists:
 * "this is the address the container binds" and "setting this to false converts a
 * durability failure into silently unlogged queries" are different conversations,
 * and one generic refusal would make the second one impossible to have.
 */
export type GuardScope =
  | "container_contract"
  | "control_plane_credential"
  | "audit_pipeline"
  | "derived_from_size"
  | "shared_edge"
  | "control_plane_ssrf"
  | "rejected_upstream";

/** GuardedKey is one entry of the guarded set: the dotted path, why it is ours,
 * and the one sentence a refusal carries. A trailing `.*` guards the whole
 * subtree. */
export interface GuardedKey {
  path: string;
  scope: GuardScope;
  /** States the consequence rather than the rule — "the edge dials this address"
   * tells a customer something they can act on; "listeners are part of the
   * container contract" does not. */
  reason: string;
}

/**
 * GUARDED_KEYS is the `SC-F613` guarded set with its reasons.
 *
 * TODO(SC-F613): this is a **mirror** of `internal/publish/guard.go`'s
 * `guardedKeys`, not a source. `GET /v1/instances/{id}/server-config` returns
 * `guarded_fields` as bare paths, and `SC-S612`'s second acceptance criterion
 * requires each displayed guarded field to carry *a short reason* — so the reasons
 * have to exist client-side until the route carries them. It is mirrored for the
 * same reason `INSTANCE_SIZES` is (`api/instances.ts`), and it carries the same
 * obligation: a key added to the Go table and not to this one renders with the
 * generic fallback in [guardReason], which is honest but says less than it should.
 *
 * The order is the Go table's, which is `06` §3.9's and `03` §2.5's, so a reviewer
 * can diff this against either document by eye.
 */
export const GUARDED_KEYS: readonly GuardedKey[] = [
  {
    path: "rest.listen",
    scope: "container_contract",
    reason: "the edge proxy dials this address; changing it makes the instance unreachable",
  },
  {
    path: "admin.listen",
    scope: "container_contract",
    reason: "the control plane dials this address to reload and to read the config digest",
  },
  {
    path: "mcp.listen",
    scope: "container_contract",
    reason: "the schema introspector dials this address",
  },
  {
    path: "admin.token",
    scope: "control_plane_credential",
    reason:
      "the control plane mints and rotates this credential; an empty value would disable admin authentication entirely with only a boot warning",
  },
  {
    path: "identity.apiKeyPepper",
    scope: "control_plane_credential",
    reason:
      "the API-key pepper is minted per instance and sealed; a known pepper makes every API-key hash on the instance precomputable",
  },
  {
    path: "audit.file.*",
    scope: "audit_pipeline",
    reason:
      "the audit file sink's layout is what makes the chain verifiable and prunable from outside the container",
  },
  {
    path: "audit.file.genesis",
    scope: "control_plane_credential",
    reason:
      "the audit genesis seed anchors the hash chain; changing it invalidates every anchored verification",
  },
  {
    path: "audit.s3.*",
    scope: "audit_pipeline",
    reason:
      "the off-node audit sink is configured from the organization's audit settings, not as a raw key",
  },
  {
    path: "audit.failClosed",
    scope: "audit_pipeline",
    reason:
      "setting this false converts a durability failure into silently unlogged queries, which is the property the audit product depends on",
  },
  {
    path: "audit.sqlSampleBytes",
    scope: "audit_pipeline",
    reason:
      "this follows the instance's audit privacy profile, which is a separate owner-only control",
  },
  {
    path: "policies.directory",
    scope: "container_contract",
    reason: "the bundle volume is mounted here read-only; the publish path writes into it",
  },
  {
    path: "policies.reload",
    scope: "container_contract",
    reason:
      "the file watcher is deliberately off: a publish renames files one at a time, and a watcher would compile the half-written directory",
  },
  {
    path: "rest.tls",
    scope: "container_contract",
    reason: "TLS terminates at the edge; the container speaks plaintext on the overlay",
  },
  {
    path: "admin.tls",
    scope: "container_contract",
    reason: "the admin plane is reachable only from the control plane over the overlay",
  },
  {
    path: "rest.trustedProxies",
    scope: "container_contract",
    reason:
      "every request arrives through the shared edge proxy, so the trusted-proxy list is the platform's",
  },
  {
    path: "limits.perIpRps",
    scope: "container_contract",
    reason:
      "every request reaches the container through the shared edge, so the only address the bucket would ever see is the proxy's — any positive value rate-limits every client of the instance as one",
  },
  {
    path: "datasources.*",
    scope: "rejected_upstream",
    reason:
      "this block is rejected or ignored upstream; datasources are configured through the datasource surface",
  },
  {
    path: "duckdb.memoryLimit",
    scope: "derived_from_size",
    reason:
      "derived from the instance size, together with the container's memory limit; editing it alone is how a container gets OOM-killed instead of spilling",
  },
  {
    path: "duckdb.threads",
    scope: "derived_from_size",
    reason: "derived from the instance size, together with the container's CPU limit",
  },
  {
    path: "approval.publicBaseUrl",
    scope: "control_plane_ssrf",
    reason: "this decides where a capability token that grants an approval decision is delivered",
  },
  {
    path: "approval.webhooks",
    scope: "control_plane_ssrf",
    reason:
      "an arbitrary POST with attacker-influenced body and attacker-selected headers; use the organization's webhook subscriptions instead, which have an SSRF validator, a signing secret and a delivery log",
  },
  {
    path: "mcp.enabled",
    scope: "shared_edge",
    reason: "MCP is exposed at the edge by an instance-level flag, not by a server setting",
  },
  {
    path: "mcp.transport",
    scope: "shared_edge",
    reason: "the transport is part of the shared router model",
  },
  {
    path: "mcp.sessionIdleMax",
    scope: "shared_edge",
    reason: "the session lifetime is part of the shared router model",
  },
  {
    path: "mcp.allowAnonymous",
    scope: "shared_edge",
    reason: "this changes the reachability of a port shared with other tenants",
  },
  {
    path: "mcp.tokenRef",
    scope: "shared_edge",
    reason:
      "a pinned MCP credential is resolved only for the stdio transport, which this product does not run",
  },
  {
    path: "mcp.apiKeyRef",
    scope: "shared_edge",
    reason:
      "a pinned MCP credential is resolved only for the stdio transport, which this product does not run",
  },
];

/**
 * guardReason resolves a dotted path to its [GuardedKey], matching a `.*` entry
 * against the subtree it guards — `audit.file.path` resolves through
 * `audit.file.*`, and so does the bare parent `audit.file`, because submitting a
 * block wholesale is submitting its children.
 *
 * A path with no entry answers a generic key rather than undefined. That is the
 * honest degradation for a server whose guarded set is wider than this bundle's
 * mirror: the field is still shown read-only and still says *that* it is
 * control-plane-owned, and only the specific consequence is missing.
 */
export function guardReason(path: string): GuardedKey {
  for (const key of GUARDED_KEYS) {
    if (key.path === path) return key;
    if (key.path.endsWith(".*")) {
      const prefix = key.path.slice(0, -2);
      if (path === prefix || path.startsWith(prefix + ".")) return key;
    }
  }
  return {
    path,
    scope: "container_contract",
    reason: "this setting is owned by Sluice Cloud and is not editable on this instance",
  };
}

// ─────────────────────────────────────────── the change classifier ──

/**
 * ChangeClass is `05` §3.11's stored vocabulary for `SC-F607`'s verdict —
 * `bundle.ChangeClass` verbatim. It decides whether publishing costs downtime,
 * and it is table-driven from one declaration of Sluice's reload boundary.
 *
 * Only two of the seven do not restart. `datasource` is the row worth reading
 * twice: its manifest travels the reload path like any other, and a catalog
 * attachment is established only while the process starts, so the union of the
 * two paths is a restart — permanently, and not merely at MVP.
 */
export type ChangeClass =
  | "none"
  | "policy_only"
  | "binding_only"
  | "datasource"
  | "server_config"
  | "secret_rotation"
  | "image_or_size";

/** PublishPath is the delivery mechanism the class implies, named so the
 * confirmation dialog can be specific. It is the empty string for a no-op, which
 * takes neither path: the generation is recorded as live and no call is issued. */
export type PublishPath = "hot_reload" | "rolling_restart" | "";

/** RESTART_CLASSES is the set of classes that replace the running task. It is
 * derived from `bundle`'s own `Restarts` column and exists so a screen never
 * re-decides the boundary from a string it happened to recognize — read
 * [Verdict.restarts] where you have a verdict, and use this only where you have a
 * bare stored class, as a generation history row does. */
export const RESTART_CLASSES: ReadonlySet<string> = new Set<string>([
  "datasource",
  "server_config",
  "secret_rotation",
  "image_or_size",
]);

/** restartClass reports whether a stored [ChangeClass] restarts the instance. */
export function restartClass(changeClass: string): boolean {
  return RESTART_CLASSES.has(changeClass);
}

/** CHANGE_CLASS_LABELS is the display word for each class, so the history, the
 * diff and the publish dialog cannot spell one class three ways. */
export const CHANGE_CLASS_LABELS: Record<string, string> = {
  none: "No change",
  policy_only: "Policies only",
  binding_only: "Subject bindings only",
  datasource: "Datasource",
  server_config: "Server settings",
  secret_rotation: "Secret rotation",
  image_or_size: "Image or size",
};

/** VerdictCause is one trigger that fired and what fired it, so the dialog can
 * list the changes that cost the outage rather than asserting one. */
export interface VerdictCause {
  /** `policy_manifest`, `subject_binding`, `datasource`, `secret`,
   * `server_config`, `image`, `size_class`, `hostname`, `force_restart` or
   * `unclassified`. */
  trigger: string;
  /** Display text: `SqlAccessPolicy/allow-support (edited)`, `small → medium`. */
  detail: string;
}

/**
 * Verdict is `SC-F607`'s full classification.
 *
 * **`reason`, `blast_radius` and `query_loss_warning` are prose the server wrote
 * and the UI renders VERBATIM.** They are not assembled in the browser and not
 * paraphrased into reassurance: `02` §5.3 is explicit that getting this copy
 * wrong in either direction — promising an outage that does not happen, or
 * failing to warn about one that does — trains users to ignore it.
 */
export interface Verdict {
  change_class: ChangeClass | string;
  publish_path: PublishPath | string;
  /** The single bit to branch on. */
  restarts: boolean;
  /** Why this classification, naming the upstream behaviour that forces it. */
  reason: string;
  /** The availability statement plus whatever the specific causes add. */
  blast_radius: string;
  /**
   * The query-loss disclosure. Non-empty **only** when this publish restarts and
   * the LIVE generation lets a query outrun Sluice's fixed drain.
   *
   * Render it verbatim. Sluice's REST drain is a hard-coded 10 s and
   * `ShutdownTimeout` is not configurable, so zero query loss on a restart-class
   * publish is not achievable today; an empty value means there is nothing to
   * disclose, never that the check was skipped.
   */
  query_loss_warning?: string;
  causes: VerdictCause[];
  /** Objects the classifier's table could not place. Non-empty only alongside a
   * fail-closed restart-class verdict. */
  unclassified?: string[];
}

// ─────────────────────────────────────────────── the publish gate ──

/** GateStepName is one rung of the `SC-F709` ladder, in ladder order. */
export type GateStepName =
  | "lint"
  | "render"
  | "config_validate"
  | "policy_validate"
  | "tests"
  | "preflight"
  | "lockout"
  | "classify";

/**
 * GateStepStatus is what the ladder reports per rung. `not-run` is a distinct
 * value from `pass` and the distinction is the honest one: `GET .../draft` skips
 * the three proxied rungs — each starts a container on the pinned image — so a UI
 * must not render an unrun step as a green tick.
 */
export type GateStepStatus = "pass" | "fail" | "warn" | "not-run" | "skipped";

/** GateStep is one rung's outcome. `detail` quotes the tool rather than
 * paraphrasing it. */
export interface GateStep {
  step: GateStepName | string;
  status: GateStepStatus | string;
  detail?: string;
}

/** GATE_STEP_LABELS names each rung for a reader. The proxied rungs say which
 * binary they run, because "validate" on its own does not distinguish the
 * control-plane linter from the pinned build's own compiler. */
export const GATE_STEP_LABELS: Record<string, string> = {
  lint: "Control-plane linter",
  render: "Deterministic render",
  config_validate: "sluice config validate (pinned image)",
  policy_validate: "sluice policy validate (pinned image)",
  tests: "sluice policy test (pinned image)",
  preflight: "Preflight checks",
  lockout: "Lockout check",
  classify: "Change classification",
};

/**
 * GateFinding is one thing the gate has to say, from whichever rung said it. One
 * shape across every step is deliberate: `SC-S713` requires the findings be
 * grouped by object, and a client cannot group a list whose members have three
 * different shapes.
 */
export interface GateFinding {
  step: GateStepName | string;
  severity: FindingSeverity;
  /** The linter's `L##`, a preflight's own tag, or absent for a proxied finding —
   * which has no rule identity, being whatever the binary printed. */
  rule?: string;
  /** `kind` and `name` anchor the finding to an object when that is recoverable.
   * A compile-stage error is a plain string with no field path at all, and
   * inventing an anchor for one would send an author to the wrong form. */
  kind?: PolicyKind | string;
  name?: string;
  path?: string;
  /** For a proxied finding this is the tool's own text, verbatim. */
  message: string;
  /** Whether an owner may proceed past it with a typed reason. False for
   * everything the pinned binary refused and for every linter error, because
   * publishing those guarantees a failed publish rather than risking one. */
  overridable: boolean;
}

/** blocksPublish reports whether a gate finding refuses the publish, mirroring
 * `policymodel.Severity.BlocksPublish`. */
export function blocksPublish(f: GateFinding): boolean {
  return f.severity === "error" || f.severity === "blocking-warning";
}

// ─────────────────────────────────────────────────────────── draft ──

/** DraftChange is one dirty object with its attribution. The label is a snapshot
 * taken at edit time, so a removed member's edits stay legible. */
export interface DraftChange {
  op: "create" | "update" | "delete";
  kind: PolicyKind | "ServerConfig" | string;
  name: string;
  changed_by: string;
  changed_by_label: string;
  changed_at: string;
}

/** DraftAuthor is the per-account roll-up the publish and discard dialogs must
 * render before they will be allowed to proceed. An empty `account` is a
 * system-originated change, which counts as somebody else's work: a change nobody
 * signed is precisely the kind a publisher should have to look at. */
export interface DraftAuthor {
  account: string;
  label: string;
  object_count: number;
  last_edit_at: string;
}

/**
 * Draft is the screen behind the publish button (`06` §3.9, `publish.DraftView`).
 *
 * `blast_radius` and `query_loss_warning` are prose written by the server
 * describing exactly what this publish costs on this instance. Render them
 * verbatim in the confirmation — they are the honest-failure text of
 * `concept.md` principle 11, not a hint.
 */
export interface Draft {
  /** False both when there is no draft and when the draft renders identically to
   * the live generation: two different states in the store, one answer to "is
   * there anything to publish". */
  dirty: boolean;
  /** The generation this draft forked from, null on an instance that has never
   * published. */
  based_on_generation: number | null;
  /** The number this draft would become. */
  generation: number;
  changes: DraftChange[];
  authors: DraftAuthor[];
  oldest_change_at?: string;
  /** The classification, flattened. The same values are on [Draft.verdict], which
   * additionally carries the per-cause list. */
  change_class: ChangeClass | string;
  publish_path: PublishPath | string;
  blast_radius: string;
  query_loss_warning?: string;
  verdict: Verdict;
  /** The draft's identity and size. The digest is what a caller echoes back as
   * `expected_digest`. */
  rendered_digest: string;
  rendered_bytes: number;
  /** Every gate finding, most severe first. */
  findings: GateFinding[];
  /** The ladder's per-step status. The proxied rungs read `not-run` here by
   * design: `GET .../draft` must not start three containers on every page load,
   * and the publish route runs the full ladder server-side. */
  gates: GateStep[];
  /** Set when the control-plane linter blocked something the pinned binary
   * accepted. Absent on this route, which does not run the binary. */
  disagreement?: string;
  /** The organization's `OD-20` setting, so the dialog can say whether a failing
   * suite will block. */
  require_tests: boolean;
  /** `OD-22`: the previous publish failed, so the next attempt must echo the
   * digest. It is on the read model because that is where a client learns it
   * without having to be refused first. */
  retry_requires_expected_digest: boolean;
}

/** getDraft reads the working set: dirty objects, per-object attribution, change
 * class, blast radius and gate findings. It is not a 404 on an instance with no
 * draft — that instance answers `dirty: false` with the live generation number. */
export function getDraft(instanceId: string): Promise<Draft> {
  return getJSON<Draft>(`${base(instanceId)}/draft`);
}

/** getDraftYaml renders the whole draft bundle as YAML, read-only (`SC-F602`). */
export async function getDraftYaml(instanceId: string): Promise<{ yaml: string; digest: string }> {
  return getJSON<{ yaml: string; digest: string }>(`${base(instanceId)}/draft/yaml`);
}

// ──────────────────────────────────────────────────────────── diff ──

/** DiffRef names a side of a diff: the draft, the live generation, or a
 * generation number. It is the spelling the route takes, so a diff URL is
 * shareable. */
export type DiffRef = "draft" | "live" | number;

/** DiffLineOp is the whole vocabulary of a unified diff line. There is no
 * "modified": a modified line is a removal followed by an addition, and
 * collapsing the two would hide which bytes moved. */
export type DiffLineOp = "context" | "added" | "removed";

/** DiffLine is one line, carrying its position on **both** sides so unified and
 * side-by-side render from one payload. The number that does not apply is zero
 * rather than absent — line numbers are 1-based everywhere in this product, so
 * zero is unambiguous. */
export interface DiffLine {
  op: DiffLineOp;
  text: string;
  old: number;
  new: number;
}

/**
 * DiffHunk is a run of changed lines with three lines of context, labelled with
 * the object it belongs to.
 *
 * The label is the point: `SC-S603` requires that the affected object's kind and
 * name label the hunk, so a reader is never left computing which object a hunk
 * belongs to. A line diff over a concatenated multi-document file cannot answer
 * that after the fact, so the renderer diffs document by document and every hunk
 * knows its object by construction.
 */
export interface DiffHunk {
  /** Empty for the file's generated header; empty `kind` alone for the rendered
   * server config. */
  kind?: PolicyKind | string;
  name?: string;
  /** The display form: `SqlAccessPolicy/allow-support`, `server.yaml`,
   * `(header)`. */
  label: string;
  /** Exactly `@@ -old_start,old_lines +new_start,new_lines @@`. */
  old_start: number;
  old_lines: number;
  new_start: number;
  new_lines: number;
  lines: DiffLine[];
}

/** DiffFile is one rendered file's change. Unchanged files are **not** present:
 * the bundle view is where the complete file list lives, and repeating it here
 * would make the common case — one policy file touched out of twelve — mostly
 * noise. */
export interface DiffFile {
  /** Bundle-relative, as it appears in the policy directory. */
  path: string;
  change: "added" | "removed" | "edited" | string;
  /** Line counts, not object counts. */
  added: number;
  removed: number;
  hunks: DiffHunk[];
}

/** DiffObject is one policy object's entry in the structured summary — the half
 * of the diff most users read, and the half a UI must be able to render without
 * re-parsing YAML. */
export interface DiffObject {
  kind: PolicyKind | string;
  name: string;
  /** The bundle file this object renders into, so the summary can link into the
   * file view without recomputing the mapping. */
  path: string;
  change: "added" | "removed" | "edited" | string;
  added: number;
  removed: number;
}

/**
 * GenerationDiff is `SC-F603`'s whole answer: what changed, per file as lines and
 * per object as structure. It mirrors `bundle.Changeset`.
 *
 * A line-level diff is sufficient for exactly one reason — both sides come from
 * the same deterministic serializer, so there is no formatting noise to filter.
 * If that invariant is ever relaxed, the diff strategy has to be revisited before
 * anything else.
 *
 * **Both sides are rendered redacted.** The diff is a read surface and the
 * rendered `server.yaml` carries a live per-instance admin bearer token, so a
 * diff that showed it would hand `/admin/reload` to anyone with a viewer role.
 * [GenerationDiff.admin_token_rotated] is how a rotation is stated without the
 * value: `~ admin.token (rotated)`, never two values and never one.
 */
export interface GenerationDiff {
  from: DiffRef;
  to: DiffRef;
  files: DiffFile[];
  objects: DiffObject[];
  /** OBJECT counts. */
  added: number;
  removed: number;
  changed: number;
  /** LINE counts. */
  lines_added: number;
  lines_removed: number;
  /** Broken out because the server config must be shown as a separate, clearly
   * labelled section: it is the one artifact in the bundle whose change is
   * restart-class (`SC-F607`, `SC-S603`). */
  server_config_changed: boolean;
  /** The one change a diff of the rendered bytes cannot show, because every read
   * surface serves the redacted text in which `admin.token` is a fixed
   * placeholder on both sides. */
  admin_token_rotated: boolean;
  /** The classification of publishing `to` over `from`, when the route computed
   * one. */
  verdict?: Verdict;
  change_class?: ChangeClass | string;
}

/**
 * getDiff compares two refs (`SC-F603`). An unknown or malformed ref answers
 * `422 validation_failed`.
 *
 * **The route is specified in `06` §3.9 and is not served by this build yet.**
 * `bundle.Diff` computes the changeset and is golden-file tested; what is missing
 * is the HTTP handler and its view type. The shape above is `bundle.Changeset`
 * under this repository's snake_case wire convention, so this function is what the
 * route has to answer — and until it does, the caller renders the `apierr` code it
 * gets back rather than a diff nobody computed. The per-generation YAML views are
 * served and are the honest fallback to offer.
 */
export function getDiff(instanceId: string, from: DiffRef, to: DiffRef): Promise<GenerationDiff> {
  return getJSON<GenerationDiff>(`${base(instanceId)}/diff${query({ from, to })}`);
}

// ───────────────────────────────────────────────────── generations ──

/**
 * GenerationState is `05` §3.11's stored spelling, verbatim, including the
 * underscore.
 *
 * `active` is the live one — not `live` — and exactly one generation per instance
 * is in it. `publish_failed` is **terminal** for that generation: the previous
 * generation stays active and the failure remains in the history rather than being
 * tidied away by a retry. Two spellings of one state is exactly the drift that
 * produces a filter which silently matches nothing.
 */
export type GenerationState =
  | "draft"
  | "validated"
  | "publishing"
  | "active"
  | "publish_failed"
  | "superseded";

/** GENERATION_STATE_LABELS is the display word for each state. */
export const GENERATION_STATE_LABELS: Record<string, string> = {
  draft: "Draft",
  validated: "Validated",
  publishing: "Publishing",
  active: "Live",
  publish_failed: "Publish failed",
  superseded: "Superseded",
};

/**
 * Generation is one snapshot with its attribution (`SC-F611`,
 * `publish.GenerationView`) — the answer to "who changed what, when, why, and did
 * it land" in one object.
 */
export interface Generation {
  /** Monotonic per instance. A generation number always means something changed:
   * publishing an unchanged draft is refused `draft_empty`. */
  generation: number;
  state: GenerationState | string;
  /** The generation this one was branched from, null for the first. */
  parent: number | null;
  created_by: string;
  /** The attribution snapshot, which survives the account (`05` §6.4). */
  created_by_label: string;
  created_at: string;
  published_at?: string;
  change_class?: ChangeClass | string;
  publish_path?: PublishPath | string;
  /** What the control plane computed. */
  rendered_digest?: string;
  /** What the instance reported back (`SC-F608`). Equal to `rendered_digest` on a
   * successful publish; that equality **is** the success condition, which is why
   * both are present even on a failure. */
  confirmed_digest?: string;
  rendered_bytes?: number;
  /** The publisher's own note — the only human record of *why* this generation
   * exists. */
  message?: string;
  /** The typed justification, present only when the publish proceeded past an
   * overridable block. An empty value is the statement that no override was
   * exercised, which is why it is omitted rather than sent empty. */
  override_reason?: string;
  /** The reload's error envelope, or the digest mismatch, verbatim. History is
   * not filtered to successes (`SC-S610`), and this field is the reason a failed
   * attempt is worth keeping. */
  failure_reason?: string;
  sluice_version?: string;
  image_digest?: string;
  /** Set the moment an incompatibility is DETECTED, so the UI can gray out an
   * impossible rollback target and say why before an operator reaches for it in an
   * incident. */
  rollback_blocked_reason?: string;
}

/**
 * listGenerations returns the publish history, newest first (`SC-F611`).
 *
 * `next` is always null: the history is capped rather than paged, because a
 * keyset cursor over a monotonic per-instance sequence buys a page boundary
 * nobody needs and costs a wasted round trip per listing.
 */
export function listGenerations(
  instanceId: string,
  params: { limit?: number; cursor?: string } = {},
): Promise<Page<Generation>> {
  return getJSON<Page<Generation>>(`${base(instanceId)}/generations${query({ ...params })}`);
}

/** getGeneration reads one generation. A non-numeric or absent `n` is `422`
 * rather than `404`: the request is malformed, and a 404 would imply the
 * generation might exist under some other spelling. */
export function getGeneration(instanceId: string, n: number): Promise<Generation> {
  return getJSON<Generation>(`${base(instanceId)}/generations/${n}`);
}

/** GenerationFile is one rendered file of a bundle, with its path as it appears
 * in the policy directory. */
export interface GenerationFile {
  path: string;
  content: string;
}

/**
 * GenerationYaml is `SC-F602`'s per-generation bundle view: every rendered file
 * plus the digests, read-only.
 *
 * **The text is redacted and the digests are not.** `server.yaml`'s `admin.token`
 * is replaced by a fixed placeholder — the route is viewer-role and Sluice
 * consumes the token verbatim rather than through `secret://` — while
 * `rendered_digest` and `server_hash` are computed over the REAL bytes, because
 * hashing the redacted text would silently break `SC-F608`'s publish
 * confirmation. [GenerationYaml.redaction_note] says exactly that, and it is
 * shown rather than summarized.
 */
export interface GenerationYaml {
  generation: number;
  files: GenerationFile[];
  rendered_digest: string;
  server_hash: string;
  redaction_note: string;
}

/** getGenerationYaml renders one generation's bundle, read-only and redacted. */
export function getGenerationYaml(instanceId: string, n: number): Promise<GenerationYaml> {
  return getJSON<GenerationYaml>(`${base(instanceId)}/generations/${n}/yaml`);
}

/**
 * downloadGenerationBundle fetches the rendered directory as a gzip tarball
 * (`SC-F619`).
 *
 * It is byte-identical to what the writer job put on the instance's policy
 * directory except for the same `admin.token` placeholder the YAML view uses, and
 * it is **the same YAML a self-hosted Sluice consumes**:
 * `sluice serve --policies-dir policies.d --config server.yaml`. Every download is
 * recorded in the control-plane trail with the generation and the requesting
 * account (`SC-S614`).
 */
export async function downloadGenerationBundle(instanceId: string, n: number): Promise<Blob> {
  const res = await request(`${base(instanceId)}/generations/${n}/bundle`);
  return res.blob();
}

/** bundleFilename is the name the download is offered under, matching the
 * `Content-Disposition` the route sets. It is derived here so a page does not
 * have to parse the header. */
export function bundleFilename(instanceName: string, n: number): string {
  const safe = instanceName.replace(/[^A-Za-z0-9._-]/g, "-") || "instance";
  return `${safe}-generation-${n}.tar.gz`;
}
